
import React, { useState } from "react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { AlertCircle, Archive } from "lucide-react";
import { BookingType } from "@/lib/supabase/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import RescheduleBookingDialog from "./dialogs/RescheduleBookingDialog";
import { isBookingPast, groupBookingsByClass } from "./bookings/utils";
import BookingGroupCard from "./bookings/BookingGroupCard";
import { useTranslation } from "react-i18next";

interface BookingsTabProps {
  bookings: BookingType[] | undefined;
  isLoading: boolean;
  error: unknown;
  onDeleteBooking: (bookingId: string, classId: string) => void;
  onUpdatePaymentStatus?: (bookingId: string, paymentStatus: 'pending' | 'paid' | 'nptr') => void;
  onRescheduleBooking?: (bookingId: string, oldClassId: string, newClassId: string) => Promise<void>;
}

const BookingsTab: React.FC<BookingsTabProps> = ({ 
  bookings, 
  isLoading, 
  error, 
  onDeleteBooking,
  onUpdatePaymentStatus,
  onRescheduleBooking
}) => {
  const { t } = useTranslation();
  const [bookingToDelete, setBookingToDelete] = useState<{id: string, classId: string} | null>(null);
  const [bookingToReschedule, setBookingToReschedule] = useState<BookingType | null>(null);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  
  const currentBookings = bookings?.filter(booking => !isBookingPast(booking)) || [];
  const pastBookings = bookings?.filter(booking => isBookingPast(booking)) || [];
  
  const currentBookingsByClass = React.useMemo(() => {
    const grouped = groupBookingsByClass(currentBookings);
    return grouped.sort((a, b) => {
      if (a.classDate && b.classDate) {
        return new Date(a.classDate).getTime() - new Date(b.classDate).getTime();
      }
      return a.className.localeCompare(b.className);
    });
  }, [currentBookings]);
  
  const pastBookingsByClass = React.useMemo(() => {
    const grouped = groupBookingsByClass(pastBookings);
    return grouped.sort((a, b) => {
      if (a.classDate && b.classDate) {
        return new Date(b.classDate).getTime() - new Date(a.classDate).getTime();
      }
      return a.className.localeCompare(b.className);
    });
  }, [pastBookings]);
  
  const handleOpenRescheduleDialog = (booking: BookingType) => {
    setBookingToReschedule(booking);
    setIsRescheduleDialogOpen(true);
  };

  const handleDeleteBooking = (bookingId: string, classId: string) => {
    setBookingToDelete({ id: bookingId, classId });
  };

  return (
    <div className="space-y-8">
      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Bookings</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : "Failed to load bookings."}
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="text-center py-6">Loading bookings...</div>
      ) : bookings && bookings.length > 0 ? (
        <Tabs defaultValue="current" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="current">Upcoming Bookings ({currentBookings.length})</TabsTrigger>
            <TabsTrigger value="history">
              <Archive className="h-4 w-4 mr-1" />
              Booking History ({pastBookings.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="current">
            {currentBookingsByClass.length > 0 ? (
              currentBookingsByClass.map(group => (
                <BookingGroupCard
                  key={group.classId}
                  group={group}
                  showActions={true}
                  onReschedule={handleOpenRescheduleDialog}
                  onDelete={handleDeleteBooking}
                  onUpdatePaymentStatus={onUpdatePaymentStatus}
                />
              ))
            ) : (
              <div className="text-center py-6">No upcoming bookings found.</div>
            )}
          </TabsContent>
          
          <TabsContent value="history">
            {pastBookingsByClass.length > 0 ? (
              pastBookingsByClass.map(group => (
                <BookingGroupCard
                  key={group.classId}
                  group={group}
                  showActions={false}
                  onUpdatePaymentStatus={onUpdatePaymentStatus}
                />
              ))
            ) : (
              <div className="text-center py-6">No booking history found.</div>
            )}
          </TabsContent>
        </Tabs>
      ) : (
        <div className="text-center py-6">No bookings found.</div>
      )}
      
      <AlertDialog 
        open={!!bookingToDelete} 
        onOpenChange={(open) => !open && setBookingToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this booking? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (bookingToDelete) {
                  onDeleteBooking(bookingToDelete.id, bookingToDelete.classId);
                  setBookingToDelete(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {onRescheduleBooking && (
        <RescheduleBookingDialog
          isOpen={isRescheduleDialogOpen}
          onOpenChange={setIsRescheduleDialogOpen}
          booking={bookingToReschedule}
          onReschedule={onRescheduleBooking}
        />
      )}
    </div>
  );
};

export default BookingsTab;
